import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";

/**
 * @slot (default) - Slot for the sidebar menu items
 * @slot header - Slot for the sidebar header
 */
@Component({
    tag: "ux-iws-sf-sidebar",
    styleUrl: "sf-sidebar.scss",
    shadow: false,
})
export class SfSidebar {
    private mediaQuery = window.matchMedia("(min-width: 768px)");
    private wasOpened: boolean = false;

    @State() isMobile: boolean = false;

    @Element() element: HTMLUxIwsSfSidebarElement;

    /** Indicates whether the sidebar is shown or not */
    @Prop({ reflect: true, mutable: true }) opened: boolean = false;

    /** Indicates whether the sidebar is fixed in the viewport (mobile only) */
    @Prop({ reflect: true, mutable: true }) fixed: boolean = false;

    @Watch("isMobile")
    handleViewportChange() {
        window.removeEventListener("scroll", this.updateFixed);
        if (this.isMobile) {
            this.updateFixed();
            window.addEventListener("scroll", this.updateFixed);
        } else {
            this.fixed = false; // Reset fixed state on desktop
        }
    }

    @Watch("opened")
    handleOpenedChange() {
        if (this.opened) {
            this.wasOpened = true;
        }
    }

    componentWillLoad() {
        this.isMobile = !this.mediaQuery.matches;
        this.opened = this.mediaQuery.matches;
        this.updateFixed();
    }

    connectedCallback() {
        this.handleViewportChange();
        this.mediaQuery.addEventListener("change", this.handleMediaQueryChange);
    }

    disconnectedCallback() {
        window.removeEventListener("scroll", this.updateFixed);
        this.mediaQuery.removeEventListener(
            "change",
            this.handleMediaQueryChange
        );
    }

    private updateFixed = () => {
        this.fixed =
            this.isMobile && this.element.parentElement
                ? this.element.parentElement.getBoundingClientRect().top < -1
                : false;
    };

    private handleMediaQueryChange = (event: MediaQueryListEvent) => {
        this.opened = event.matches;
        this.isMobile = !event.matches;
    };

    private toggleSidebar = () => {
        this.opened = !this.opened;
    };

    render() {
        return (
            <Host data-animated={this.wasOpened ? "true" : undefined}>
                {this.isMobile && (
                    <button
                        class="ux-iws-sf-sidebar__tab"
                        aria-label={
                            this.opened ? "Close sidebar" : "Open sidebar"
                        }
                        onClick={this.toggleSidebar}
                    >
                        <slot name="icon">
                            <ux-icon name="avatar-circle" weight="bold" />
                        </slot>
                    </button>
                )}
                <div role="presentation" class="ux-iws-sf-sidebar__inner">
                    <header>
                        <slot name="header" />
                    </header>
                    <nav role="menu">
                        <slot />
                    </nav>
                    {this.isMobile && this.opened && (
                        <button
                            class="ux-iws-sf-sidebar__close"
                            aria-label="Close sidebar"
                            onClick={this.toggleSidebar}
                        >
                            <ux-icon name="cross" weight="bold" />
                        </button>
                    )}
                </div>
            </Host>
        );
    }
}
